import { SearchOutlined } from '@ant-design/icons';
import { DynamicBlock } from "@components/block";
import { SingleHeader } from "@components/header";
import { FilterIcon } from "@components/icons/Icons";
import { ClearFiltersBar } from "@components/issues-filter-drawer";
import { PostsFiltersDrawer } from "@components/posts-filter-drawer";
import { SCREEN, useWindowSize } from "@components/window-size-provider";
import { QueryParms } from "@lib/constants";
import { deleteAttr, sanitizeSearchInput, scrollToThen } from "@lib/utils";
import { getWordpressVars } from "@lib/wordpressVars";
import { Button, Col, Input, Layout, Row, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "swiper/swiper.min.css";
import PostSearchTable from "../../partials/post/PostSearchTable";
import { useGetPostsSearchQuery } from '@mwServices/Post';

const { Search } = Input;

const SortParamMap = {
    'ascend': 'asc',
    'descend': 'desc',
    'asc': 'ascend',
    'desc': 'descend',
    'title': QueryParms.ORDER_TITLE,
    'date': QueryParms.ORDER_DATE,
    'description': QueryParms.ORDER_EXCERPT,
    'category': QueryParms.ORDER_CATEGORY,
}

const CollectorContentSearch = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const itemCount = 20; //= usePreferences();
    const windowDimensions = useWindowSize();
    const { title, image, ads } = getWordpressVars();
    const [showDrawer, setShowDrawer] = useState<boolean>(false);
    const [inputSearchValue, setInputSearchValue] = useState<any>('')

    const postParams: any = useMemo(() => {
        let search = searchParams.has('search')
            ? sanitizeSearchInput(searchParams.get('search'))
            : '';
        return (
            deleteAttr({
                ...{
                    [QueryParms.SEARCH]: search,
                    [QueryParms.PAGE]: 1,
                    [QueryParms.PER_PAGE]: itemCount,
                    [QueryParms.TERMS]: null,
                    [QueryParms.ORDER_TITLE]: null,
                    [QueryParms.ORDER_DATE]: null,
                    [QueryParms.ORDER_EXCERPT]: null,
                    [QueryParms.ORDER_CATEGORY]: null,
                },
                ...Object.fromEntries([...searchParams])
            }, ['']))
    }, [searchParams]);

    const postQuery = useGetPostsSearchQuery({
        ...postParams,
        parts: [
            'ID', 'title', 'date', 'taxonomy',
            'description', 'link', 'image',
        ]
    });

    const pagination = postQuery?.data?.pagination
        ? postQuery.data?.pagination
        : null;

    useEffect(() => {
        setInputSearchValue(postParams.search);
    }, [postParams])

    const onHandleInputSearch = (value: string) => {

        setInputSearchValue(value);

        setSearchParams(params => {
            params.set(QueryParms.PAGE, `1`);
            params.set(QueryParms.PER_PAGE, `${itemCount}`);

            if (postParams[QueryParms.TERMS]) params.set(
                QueryParms.TERMS,
                postParams[QueryParms.TERMS]
                    .split(',')
            );

            params.set(
                QueryParms.SEARCH,
                sanitizeSearchInput(value)
            );

            return params;
        });

    };

    const onHandleDrawerSearch = (e, filters) => {
        setSearchParams(params => {
            let proccessFilters = deleteAttr(filters);

            let terms: any = [
                ...new Set([
                    ...proccessFilters.categories,
                    ...proccessFilters.tags])
            ];
            if (proccessFilters.categories ||
                proccessFilters.tags) params.set(
                    QueryParms.TERMS, terms
                );

            if (proccessFilters.dates) params.set(
                QueryParms.DATES, proccessFilters.dates
            );

            params.delete(QueryParms.ORDER_TITLE);
            params.delete(QueryParms.ORDER_DATE);
            params.delete(QueryParms.ORDER_EXCERPT);
            params.delete(QueryParms.ORDER_CATEGORY);

            params.set(QueryParms.PAGE, `1`);

            return params;
        });
    };

    const handleClearFilters = () => {
        setInputSearchValue('');

        setSearchParams(params => {
            params.delete(QueryParms.SEARCH);
            params.delete(QueryParms.TERMS);
            params.delete(QueryParms.DATES);
            params.delete(QueryParms.ORDER_TITLE);
            params.delete(QueryParms.ORDER_DATE);
            params.delete(QueryParms.ORDER_EXCERPT);
            params.delete(QueryParms.ORDER_CATEGORY);
            return params;
        });
    };

    const handleOnSortFilters = (sorter) => {
        setSearchParams(params => {
            if (SortParamMap[sorter.order] === undefined
                && params.has(SortParamMap[sorter.columnKey])) {
                params.delete(SortParamMap[sorter.columnKey]);
            } else {
                params.set(
                    SortParamMap[sorter.columnKey],
                    SortParamMap[sorter.order]
                )
            }
            return params;
        });
    };

    const handleOnPaginationChange = (page) => {
        scrollToThen({ top: 0, behavior: 'smooth' }, () => {
            setSearchParams(params => {
                params.set(QueryParms.PAGE, `${page}`);
                params.set(QueryParms.PER_PAGE, `${itemCount}`);
                return params;
            });
        });
    };

    return (
        <Layout className="collector-content-search">
            <Row justify={`center`}>
                <Col span={24}>
                    <Spin
                        spinning={
                            postQuery.isFetching ||
                            postQuery.isLoading
                        }
                        size="large"
                    >
                        <SingleHeader
                            className="hero-header"
                            title={title}
                            imgSrc={image.src}
                            focalPoint={image.focalPoint}
                            justify="center"
                            parallax={false}
                        />
                    </Spin>
                </Col>
            </Row>
            <Row justify={`center`} gutter={[16, 16]}>
                <Col xs={22} sm={22} md={22}>
                    <Row
                        className="search-input-wrapper"
                        wrap={false}
                        gutter={[5, 5]}
                    >
                        <Col flex={`auto`}>
                            <Search
                                loading={postQuery.isFetching || postQuery.isLoading}
                                prefix={<SearchOutlined />}
                                onSearch={(value) => onHandleInputSearch(value)}
                                onChange={(e) => setInputSearchValue(e.target.value)}
                                defaultValue={inputSearchValue}
                                value={inputSearchValue}
                                className="search-input"
                                placeholder="Search"
                                enterButton="GO"
                                size="large"
                                allowClear
                            />
                        </Col>
                        <Col flex={`none`}>
                            <Button
                                onClick={() => setShowDrawer(true)}
                                className="greyButton"
                                size="large"
                            >
                                {windowDimensions.up(SCREEN.SM)
                                    && (<span className="filterButtonText">Filter</span>)}
                                <FilterIcon className="font-12" />
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row justify={`center`} gutter={[16, 16]} >
                <Col span={22}>
                    {postQuery?.data &&
                        <ClearFiltersBar
                            className={`mar-top-0 mar-bot-30`}
                            issueCount={pagination?.total_items}
                            filters={postParams?.terms}
                            onClear={handleClearFilters}
                            defaultFilters={{}}
                        />}
                </Col>
            </Row>

            <Row justify={`center`}>
                <Col xs={23} lg={22}>
                    <Row
                        className={
                            windowDimensions.isLargeScreen
                                ? 'grid-auto-350'
                                : ''
                        }
                        gutter={[30, 30]}
                    >
                        <Col className={`border-r-1-light-grey`}>
                            <Spin
                                size="large"
                                spinning={postQuery.isFetching || postQuery.isLoading}
                            >
                                <Row justify={"center"} className={`mar-bot-30`}>
                                    <Col span={24}>
                                        {postQuery?.data &&
                                            <PostSearchTable
                                                pagination={pagination}
                                                search={sanitizeSearchInput(postParams[QueryParms.SEARCH])}
                                                data={postQuery.data.results}
                                                onSortChange={(sorter) => handleOnSortFilters(sorter)}
                                                onPaginationChange={(page) => handleOnPaginationChange(page)}
                                                sortDefaults={{
                                                    [QueryParms.ORDER_TITLE]: SortParamMap[postParams[QueryParms.ORDER_TITLE]],
                                                    [QueryParms.ORDER_DATE]: SortParamMap[postParams[QueryParms.ORDER_DATE]],
                                                    [QueryParms.ORDER_EXCERPT]: SortParamMap[postParams[QueryParms.ORDER_EXCERPT]],
                                                    [QueryParms.ORDER_CATEGORY]: SortParamMap[postParams[QueryParms.ORDER_CATEGORY]],
                                                }}
                                            />}
                                    </Col>
                                </Row>
                            </Spin>
                        </Col>
                        <Col>
                            {ads && <DynamicBlock blocks={ads} />}
                        </Col>
                    </Row>
                </Col>
            </Row>
            <PostsFiltersDrawer
                onDrawerSearch={onHandleDrawerSearch}
                onClose={() => setShowDrawer(false)}
                visible={showDrawer}
                filters={{
                    ...postParams,
                    dates: postParams?.dates
                        ?.split(','),
                    terms: postParams?.terms
                        ?.split(',')
                        ?.filter(Boolean)
                        ?.map(Number) || []
                }
                }
            />
        </Layout>
    );
};

export default CollectorContentSearch;

